import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { createHttpClient } from '@shared/api/httpClient';
import { getRuntimeConfig } from '@shared/config/runtime';
import { getSignedInUserEmail, isClientAdmin } from '@shared/auth/cognito';
import type {
  ClientUserRecord,
  ClientUsersManagementResponse,
  CreateClientUserResponse,
  DeleteClientUserResponse,
  DeleteWorkSpacesUserResponse,
  UpdateClientUserResponse,
  UpsertWorkSpacesUserResponse,
  WorkSpacesDesktopProfileOption,
  WorkSpacesUserRecord,
} from '@shared/types/api';
import { displayNameForIdentity } from '@shared/utils/privacy';
import { statusPillClassName } from '@shared/utils/statusPill';

type UsersTab = 'portal' | 'workspaces';

type PortalUserDraft = {
  email: string;
  groupName: string;
  name: string;
  givenName: string;
  familyName: string;
};

type WorkSpacesUserDraft = {
  userName: string;
  givenName: string;
  surname: string;
  email: string;
  desktopProfile: string;
};

const DESKTOP_PROFILE_OPTIONS: WorkSpacesDesktopProfileOption[] = [
  { value: 'standard', label: 'Standard', description: 'Light or occasional office user.', operatingSystem: 'Windows', cpu: '2 vCPU', memory: '4 GiB', storage: 'Root 80 GB / User 50 GB' },
  { value: 'performance', label: 'Performance', description: 'Recommended default for most CUI desktop users.', operatingSystem: 'Windows', cpu: '2 vCPU', memory: '8 GiB', storage: 'Root 80 GB / User 100 GB' },
  { value: 'power', label: 'Power', description: 'Admin, technical, or heavier multitasking user.', operatingSystem: 'Windows', cpu: '4 vCPU', memory: '16 GiB', storage: 'Root 175 GB / User 100 GB' },
  { value: 'powerpro', label: 'PowerPro', description: 'Heavy spreadsheets, BI, development, or advanced technical work.', operatingSystem: 'Windows', cpu: '8 vCPU', memory: '32 GiB', storage: 'Root 175 GB / User 100 GB' },
  { value: 'graphics-g6-xlarge', label: 'Graphics', description: 'Specialized engineering, CAD, or graphics-heavy workloads.', operatingSystem: 'Windows', cpu: '4 vCPU', memory: '16 GiB', gpu: 'Graphics accelerated' },
];

const emptyPortalUserDraft: PortalUserDraft = {
  email: '',
  groupName: '',
  name: '',
  givenName: '',
  familyName: '',
};

const emptyWorkSpacesUserDraft: WorkSpacesUserDraft = {
  userName: '',
  givenName: '',
  surname: '',
  email: '',
  desktopProfile: 'performance',
};

function valueOrDash(value: unknown): string {
  if (value === null || value === undefined) return '—';
  const text = String(value).trim();
  return text || '—';
}

function formatDate(value?: string): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function portalUserDisplayName(user: ClientUserRecord): string {
  return displayNameForIdentity(user as unknown as Record<string, unknown>, user.email || user.username || 'Portal user');
}

function portalUserStatus(user: ClientUserRecord): string {
  if (user.enabled === false) return 'DISABLED';
  return valueOrDash(user.userStatus || (user.enabled ? 'ENABLED' : 'UNKNOWN'));
}

function portalUserRole(user: ClientUserRecord, clientAdminGroupName: string, clientReadonlyGroupName: string): string {
  const groupName = valueOrDash(user.groupName || user.groups?.[0]);
  if (groupName === clientAdminGroupName) return 'Client Admin';
  if (groupName === clientReadonlyGroupName) return 'Client Read Only';
  return groupName;
}

function workspaceUserName(user: WorkSpacesUserRecord): string {
  return valueOrDash(user.userName || user.user_name || user.username);
}

function workspaceDisplayName(user: WorkSpacesUserRecord): string {
  const givenName = valueOrDash(user.givenName || user.given_name);
  const surname = valueOrDash(user.surname || user.familyName || user.family_name);
  if (givenName !== '—' || surname !== '—') {
    return [givenName === '—' ? '' : givenName, surname === '—' ? '' : surname].filter(Boolean).join(' ');
  }
  return workspaceUserName(user);
}

function workspaceStatus(user: WorkSpacesUserRecord): string {
  return valueOrDash(user.workspaceStatus || user.lifecycleStatus || user.status || 'PENDING DEPLOYMENT');
}

function normalizedDesktopProfile(value?: string): string {
  return String(value || '').trim().toLowerCase();
}

function desktopProfileOptionFor(value: string | undefined, options: WorkSpacesDesktopProfileOption[]): WorkSpacesDesktopProfileOption | undefined {
  const normalized = normalizedDesktopProfile(value || 'performance');
  return options.find((candidate) => normalizedDesktopProfile(candidate.value || candidate.profile) === normalized)
    ?? options.find((candidate) => normalizedDesktopProfile(candidate.profile) === normalized)
    ?? DESKTOP_PROFILE_OPTIONS.find((candidate) => candidate.value === normalized);
}

function desktopProfileLabel(value: string | undefined, options: WorkSpacesDesktopProfileOption[]): string {
  const option = desktopProfileOptionFor(value, options);
  return option?.label || valueOrDash(value || 'performance');
}

function desktopProfileSpecs(option?: WorkSpacesDesktopProfileOption): Array<[string, string]> {
  if (!option) return [];
  const storage = option.storage || [
    option.rootStorageGb ? `Root ${option.rootStorageGb} GB` : '',
    option.userStorageGb ? `User ${option.userStorageGb} GB` : '',
  ].filter(Boolean).join(' / ');
  const rows: Array<[string, string]> = [
    ['O/S', option.operatingSystem || 'Windows'],
    ['CPU', option.cpu || option.computeType || '—'],
    ['Memory', option.memory || '—'],
    ['Storage', storage || '—'],
    ['Graphics', option.gpu || '—'],
  ];
  return rows.filter(([, value]) => value !== '—');
}

function workspaceDesktopProfile(user: WorkSpacesUserRecord): string {
  return normalizedDesktopProfile(user.desktopProfile || user.bundleProfile || user.bundle_profile || 'performance') || 'performance';
}

export default function UsersPage() {
  const runtime = getRuntimeConfig();
  const clientAdmin = isClientAdmin();
  const signedInEmail = getSignedInUserEmail()?.toLowerCase() || '';
  const api = useMemo(() => createHttpClient({ portal: 'client' }), []);
  const [activeTab, setActiveTab] = useState<UsersTab>('portal');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [response, setResponse] = useState<ClientUsersManagementResponse | null>(null);
  const [editingPortalUsername, setEditingPortalUsername] = useState<string | null>(null);
  const [portalDraft, setPortalDraft] = useState<PortalUserDraft>(() => ({
    ...emptyPortalUserDraft,
    groupName: runtime.clientReadonlyGroupName,
  }));
  const [workspacesDraft, setWorkspacesDraft] = useState<WorkSpacesUserDraft>(emptyWorkSpacesUserDraft);

  const loadUsers = useCallback(async () => {
    if (!clientAdmin) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const nextResponse = await api.get<ClientUsersManagementResponse>('/client/users');
      setResponse(nextResponse);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Unable to load users.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [api, clientAdmin]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const portalUsers = response?.portalUsers ?? response?.users ?? [];
  const workspacesUsers = response?.workspacesUsers ?? response?.workspaceUsers ?? [];
  const desktopProfileOptions = response?.desktopProfileOptions?.length ? response.desktopProfileOptions : DESKTOP_PROFILE_OPTIONS;
  const selectedDesktopProfile = desktopProfileOptionFor(workspacesDraft.desktopProfile, desktopProfileOptions);
  const featureEnabled = Boolean(response?.enabled && response?.canManage);
  const editingPortalUser = portalUsers.find((user) => user.username === editingPortalUsername) || null;

  function resetPortalForm() {
    setEditingPortalUsername(null);
    setPortalDraft({ ...emptyPortalUserDraft, groupName: runtime.clientReadonlyGroupName });
  }

  function isSelfPortalUser(user: ClientUserRecord): boolean {
    const userEmail = String(user.email || '').trim().toLowerCase();
    const username = String(user.username || '').trim().toLowerCase();
    return Boolean(signedInEmail && (userEmail === signedInEmail || username === signedInEmail));
  }

  function editPortalUser(user: ClientUserRecord) {
    setEditingPortalUsername(user.username || null);
    setPortalDraft({
      email: user.email || user.username || '',
      groupName: user.groupName || user.groups?.find((group) => group === runtime.clientAdminGroupName || group === runtime.clientReadonlyGroupName) || runtime.clientReadonlyGroupName,
      name: user.name || '',
      givenName: user.givenName || '',
      familyName: user.familyName || '',
    });
    setActiveTab('portal');
  }

  async function handleSavePortalUser(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      if (editingPortalUsername) {
        const result = await api.patch<UpdateClientUserResponse>(`/client/users/${encodeURIComponent(editingPortalUsername)}`, {
          groupName: portalDraft.groupName,
          name: portalDraft.name.trim() || undefined,
          givenName: portalDraft.givenName.trim() || undefined,
          familyName: portalDraft.familyName.trim() || undefined,
        });
        setNotice(result.message || 'Portal user updated.');
      } else {
        const result = await api.post<CreateClientUserResponse>('/client/users', {
          email: portalDraft.email.trim(),
          groupName: portalDraft.groupName,
          name: portalDraft.name.trim() || undefined,
          givenName: portalDraft.givenName.trim() || undefined,
          familyName: portalDraft.familyName.trim() || undefined,
        });
        setNotice(result.message || 'Portal user invited.');
      }
      resetPortalForm();
      await loadUsers();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to save portal user.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePortalAction(user: ClientUserRecord, action: 'enable' | 'disable' | 'reset-mfa' | 'resend-invite') {
    const username = user.username;
    if (!username) return;
    const actionLabel = action === 'reset-mfa' ? 'reset MFA for' : action === 'resend-invite' ? 'resend invite for' : action;
    if (action === 'disable') {
      const confirmed = window.confirm(`Disable ${portalUserDisplayName(user)}? Their active sessions will be signed out.`);
      if (!confirmed) return;
    }
    if (action === 'resend-invite') {
      const confirmed = window.confirm(`Resend the portal invitation for ${valueOrDash(user.email || user.username)}? This may refresh the temporary password invitation for users who have not completed onboarding.`);
      if (!confirmed) return;
    }

    setBusyKey(`portal-${action}-${username}`);
    setError(null);
    setNotice(null);
    try {
      const result = await api.post<UpdateClientUserResponse>(`/client/users/${encodeURIComponent(username)}/${action}`);
      setNotice(result.message || `Portal user ${actionLabel} complete.`);
      await loadUsers();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : `Unable to ${actionLabel} portal user.`);
    } finally {
      setBusyKey(null);
    }
  }

  async function handleDeletePortalUser(user: ClientUserRecord) {
    const username = user.username;
    if (!username) return;
    if (isSelfPortalUser(user)) {
      setError('You cannot remove your own portal account.');
      return;
    }
    const confirmed = window.confirm(`Remove portal access for ${portalUserDisplayName(user)}? This deletes their portal login. Audit history remains tied to prior activity records.`);
    if (!confirmed) return;

    setBusyKey(`portal-delete-${username}`);
    setError(null);
    setNotice(null);
    try {
      const result = await api.delete<DeleteClientUserResponse>(`/client/users/${encodeURIComponent(username)}`);
      setNotice(result.message || 'Portal user removed.');
      if (editingPortalUsername === username) resetPortalForm();
      await loadUsers();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to remove portal user.');
    } finally {
      setBusyKey(null);
    }
  }

  function editWorkSpacesUser(user: WorkSpacesUserRecord) {
    setWorkspacesDraft({
      userName: workspaceUserName(user) === '—' ? '' : workspaceUserName(user),
      givenName: valueOrDash(user.givenName || user.given_name) === '—' ? '' : valueOrDash(user.givenName || user.given_name),
      surname: valueOrDash(user.surname || user.familyName || user.family_name) === '—' ? '' : valueOrDash(user.surname || user.familyName || user.family_name),
      email: valueOrDash(user.email) === '—' ? '' : valueOrDash(user.email),
      desktopProfile: workspaceDesktopProfile(user),
    });
    setActiveTab('workspaces');
  }

  async function handleSaveWorkSpacesUser(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.post<UpsertWorkSpacesUserResponse>('/client/workspace-users', {
        userName: workspacesDraft.userName.trim(),
        givenName: workspacesDraft.givenName.trim() || undefined,
        surname: workspacesDraft.surname.trim() || undefined,
        email: workspacesDraft.email.trim() || undefined,
        desktopProfile: workspacesDraft.desktopProfile || 'performance',
      });
      setNotice(result.message || 'WorkSpaces user saved.');
      setWorkspacesDraft(emptyWorkSpacesUserDraft);
      await loadUsers();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to save WorkSpaces user.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeleteWorkSpacesUser(user: WorkSpacesUserRecord) {
    const username = workspaceUserName(user);
    if (!username || username === '—') return;
    const confirmed = window.confirm(`Remove WorkSpaces user ${username}? Desktop provisioning changes require an approved SURELINC deployment before they take effect.`);
    if (!confirmed) return;

    setBusyKey(`workspace-delete-${username}`);
    setError(null);
    setNotice(null);
    try {
      const result = await api.delete<DeleteWorkSpacesUserResponse>(`/client/workspace-users/${encodeURIComponent(username)}`);
      setNotice(result.message || 'WorkSpaces user removed.');
      await loadUsers();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to remove WorkSpaces user.');
    } finally {
      setBusyKey(null);
    }
  }

  async function handleWorkSpacesAction(user: WorkSpacesUserRecord, action: 'enable' | 'disable') {
    const username = workspaceUserName(user);
    if (!username || username === '—') return;
    const confirmed = window.confirm(`${action === 'enable' ? 'Enable' : 'Disable'} WorkSpaces user ${username}? Desktop provisioning changes require an approved SURELINC deployment before they take effect.`);
    if (!confirmed) return;

    setBusyKey(`workspace-${action}-${username}`);
    setError(null);
    setNotice(null);
    try {
      const result = await api.post<UpsertWorkSpacesUserResponse>(`/client/workspace-users/${encodeURIComponent(username)}/${action}`);
      setNotice(result.message || `WorkSpaces user ${action === 'enable' ? 'enabled' : 'disabled'}.`);
      await loadUsers();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : `Unable to ${action} WorkSpaces user.`);
    } finally {
      setBusyKey(null);
    }
  }

  if (!clientAdmin) {
    return (
      <div className="card">
        <h2>Users</h2>
        <p className="muted-text">Client administrator access is required to manage users.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="card">
        <h2>Users</h2>
        <p className="muted-text">Loading users…</p>
      </div>
    );
  }

  if (!featureEnabled) {
    return (
      <div className="card">
        <h2>Users</h2>
        {error ? <p className="error-text">{error}</p> : null}
        <p className="muted-text">{response?.message || 'User management is not enabled for this client.'}</p>
        <p className="muted-text">Ask SURELINC to enable the Client Users page when this client should manage portal users or WorkSpaces users.</p>
      </div>
    );
  }

  return (
    <div className="users-page-stack">
      <div className="page-heading-with-actions">
        <div>
          <h2>Users</h2>
          <p className="muted-text">Manage SURELINC portal access and approved WorkSpaces user requests for {valueOrDash(response?.clientName)}.</p>
        </div>
        <button className="secondary-button" type="button" onClick={() => void loadUsers()} disabled={loading || submitting || Boolean(busyKey)}>
          Refresh
        </button>
      </div>

      {error ? <div className="card users-alert users-alert-error"><p className="error-text">{error}</p></div> : null}
      {notice ? <div className="card users-alert users-alert-success"><p className="success-text">{notice}</p></div> : null}

      <div className="users-summary-grid">
        <div className="card metric-card metric-blue">
          <small>Portal users</small>
          <strong>{portalUsers.length}</strong>
        </div>
        <div className="card metric-card metric-blue">
          <small>WorkSpaces users</small>
          <strong>{workspacesUsers.length}</strong>
        </div>
      </div>

      <div className="card">
        <div className="tabs" role="tablist" aria-label="Users sections">
          <button className={`tab-button ${activeTab === 'portal' ? 'tab-button-active' : ''}`} type="button" onClick={() => setActiveTab('portal')} role="tab" aria-selected={activeTab === 'portal'}>
            Portal Users
          </button>
          <button className={`tab-button ${activeTab === 'workspaces' ? 'tab-button-active' : ''}`} type="button" onClick={() => setActiveTab('workspaces')} role="tab" aria-selected={activeTab === 'workspaces'}>
            WorkSpaces Users
          </button>
        </div>

        {activeTab === 'portal' ? (
          <div className="tab-panel-stack" role="tabpanel">
            <div>
              <h3>{editingPortalUsername ? 'Edit portal user' : 'Invite portal user'}</h3>
              <p className="muted-text">
                {editingPortalUsername
                  ? 'Update the user name fields or portal role. Remove the user and invite the correct email if the email address was entered incorrectly.'
                  : 'Invite client administrators or read-only users to the SURELINC client portal.'}
              </p>
            </div>
            <form className="form-grid users-form-grid portal-user-form-grid" onSubmit={handleSavePortalUser}>
              <label className="portal-email-field">
                Email
                <input required type="email" value={portalDraft.email} disabled={Boolean(editingPortalUsername)} onChange={(event) => setPortalDraft((current) => ({ ...current, email: event.target.value }))} placeholder="user@example.com" />
                {editingPortalUsername ? <span className="field-help">Email is the portal sign-in name. Remove and re-invite the user if the email address is wrong.</span> : null}
              </label>
              <label>
                Role
                <select value={portalDraft.groupName} onChange={(event) => setPortalDraft((current) => ({ ...current, groupName: event.target.value }))}>
                  <option value={runtime.clientReadonlyGroupName}>Client Read Only</option>
                  <option value={runtime.clientAdminGroupName}>Client Admin</option>
                </select>
              </label>
              <label>
                First name
                <input value={portalDraft.givenName} onChange={(event) => setPortalDraft((current) => ({ ...current, givenName: event.target.value }))} placeholder="Jane" />
              </label>
              <label>
                Last name
                <input value={portalDraft.familyName} onChange={(event) => setPortalDraft((current) => ({ ...current, familyName: event.target.value }))} placeholder="Doe" />
              </label>
              <label className="portal-display-name-field">
                Display name
                <input value={portalDraft.name} onChange={(event) => setPortalDraft((current) => ({ ...current, name: event.target.value }))} placeholder="Jane Doe" />
              </label>
              <div className="form-actions form-wide">
                <button className="primary-button" type="submit" disabled={submitting}>{editingPortalUsername ? 'Update portal user' : 'Invite portal user'}</button>
                <button className="secondary-button" type="button" onClick={resetPortalForm} disabled={submitting}>{editingPortalUsername ? 'Cancel edit' : 'Clear'}</button>
                <span className="field-help">{editingPortalUsername ? `Editing ${portalUserDisplayName(editingPortalUser || { username: editingPortalUsername })}.` : 'New users receive the SURELINC welcome email and must complete the temporary-password sign-in flow.'}</span>
              </div>
            </form>

            <div className="table-scroll">
              <table className="data-table users-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {portalUsers.map((user) => {
                    const username = user.username;
                    const selfUser = isSelfPortalUser(user);
                    return (
                      <tr key={username || user.email}>
                        <td>{portalUserDisplayName(user)}{selfUser ? <span className="inline-note"> You</span> : null}</td>
                        <td>{valueOrDash(user.email)}</td>
                        <td>{portalUserRole(user, runtime.clientAdminGroupName, runtime.clientReadonlyGroupName)}</td>
                        <td><span className={statusPillClassName(portalUserStatus(user))}>{portalUserStatus(user)}</span></td>
                        <td>{formatDate(user.createdAt)}</td>
                        <td>
                          <div className="button-row">
                            <button className="secondary-button compact-action-button" type="button" onClick={() => editPortalUser(user)} disabled={!username || Boolean(busyKey)}>
                              Edit
                            </button>
                            {user.enabled === false ? (
                              <button className="secondary-button compact-action-button" type="button" onClick={() => void handlePortalAction(user, 'enable')} disabled={!username || Boolean(busyKey)}>
                                Enable
                              </button>
                            ) : (
                              <button className="secondary-button compact-action-button" type="button" onClick={() => void handlePortalAction(user, 'disable')} disabled={!username || selfUser || Boolean(busyKey)}>
                                Disable
                              </button>
                            )}
                            <button className="secondary-button compact-action-button" type="button" onClick={() => void handlePortalAction(user, 'resend-invite')} disabled={!username || Boolean(busyKey)}>
                              Resend Invite
                            </button>
                            <button className="secondary-button compact-action-button" type="button" onClick={() => void handlePortalAction(user, 'reset-mfa')} disabled={!username || Boolean(busyKey)}>
                              Reset MFA
                            </button>
                            <button className="danger-button compact-action-button" type="button" onClick={() => void handleDeletePortalUser(user)} disabled={!username || selfUser || Boolean(busyKey)}>
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {portalUsers.length === 0 ? (
                    <tr><td colSpan={6} className="muted-text">No portal users found.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="tab-panel-stack" role="tabpanel">
            <div>
              <h3>WorkSpaces users</h3>
              <p className="muted-text">Request user additions or updates for managed Windows WorkSpaces without exposing infrastructure details. Saved changes require an approved SURELINC deployment before desktop provisioning changes take effect.</p>
            </div>
            <form className="form-grid users-form-grid client-workspaces-user-form-grid" onSubmit={handleSaveWorkSpacesUser}>
              <label className="workspace-username-field">
                User name
                <input required pattern="[A-Za-z0-9._-]{1,64}" value={workspacesDraft.userName} onChange={(event) => setWorkspacesDraft((current) => ({ ...current, userName: event.target.value }))} placeholder="jdoe" />
                <span className="field-help">Use letters, numbers, periods, underscores, and hyphens.</span>
              </label>
              <label className="client-desktop-profile-field">
                Desktop profile
                <select value={workspacesDraft.desktopProfile} onChange={(event) => setWorkspacesDraft((current) => ({ ...current, desktopProfile: event.target.value }))}>
                  {desktopProfileOptions.map((option) => (
                    <option key={option.value || option.profile || option.label} value={option.value || option.profile || ''}>{option.label}</option>
                  ))}
                </select>
                {selectedDesktopProfile ? (
                  <div className="desktop-profile-spec-card">
                    <strong>{selectedDesktopProfile.description || 'Selected desktop profile'}</strong>
                    <dl>
                      {desktopProfileSpecs(selectedDesktopProfile).map(([label, value]) => (
                        <div key={label}>
                          <dt>{label}</dt>
                          <dd>{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ) : null}
                <span className="field-help">Performance is the recommended default. SURELINC maps this friendly profile to the correct regional WorkSpaces bundle during deployment.</span>
              </label>
              <label className="workspace-first-name-field">
                First name
                <input value={workspacesDraft.givenName} onChange={(event) => setWorkspacesDraft((current) => ({ ...current, givenName: event.target.value }))} placeholder="Jane" />
              </label>
              <label className="workspace-last-name-field">
                Last name
                <input value={workspacesDraft.surname} onChange={(event) => setWorkspacesDraft((current) => ({ ...current, surname: event.target.value }))} placeholder="Doe" />
              </label>
              <label className="workspace-email-field">
                Email
                <input type="email" value={workspacesDraft.email} onChange={(event) => setWorkspacesDraft((current) => ({ ...current, email: event.target.value }))} placeholder="jane@example.com" />
              </label>
              <div className="form-actions form-wide">
                <button className="primary-button" type="submit" disabled={submitting}>Save WorkSpaces user</button>
                <button className="secondary-button" type="button" onClick={() => setWorkspacesDraft(emptyWorkSpacesUserDraft)} disabled={submitting}>Clear</button>
              </div>
            </form>

            <div className="table-scroll">
              <table className="data-table users-table">
                <thead>
                  <tr>
                    <th>User name</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Desktop profile</th>
                    <th>Status</th>
                    <th>Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {workspacesUsers.map((user) => {
                    const username = workspaceUserName(user);
                    return (
                      <tr key={username}>
                        <td className="mono-cell">{username}</td>
                        <td>{workspaceDisplayName(user)}</td>
                        <td>{valueOrDash(user.email)}</td>
                        <td>{desktopProfileLabel(workspaceDesktopProfile(user), desktopProfileOptions)}</td>
                        <td><span className={statusPillClassName(workspaceStatus(user))}>{workspaceStatus(user)}</span></td>
                        <td>{formatDate(user.updatedAt || user.createdAt)}</td>
                        <td>
                          <div className="button-row">
                            <button className="secondary-button compact-action-button" type="button" onClick={() => editWorkSpacesUser(user)} disabled={Boolean(busyKey)}>Edit</button>
                            {String(user.lifecycleStatus || user.status || '').toUpperCase() === 'DISABLED' ? (
                              <button className="secondary-button compact-action-button" type="button" onClick={() => void handleWorkSpacesAction(user, 'enable')} disabled={Boolean(busyKey)}>Enable</button>
                            ) : (
                              <button className="secondary-button compact-action-button" type="button" onClick={() => void handleWorkSpacesAction(user, 'disable')} disabled={Boolean(busyKey)}>Disable</button>
                            )}
                            <button className="secondary-button compact-action-button" type="button" onClick={() => void handleDeleteWorkSpacesUser(user)} disabled={Boolean(busyKey)}>Remove</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {workspacesUsers.length === 0 ? (
                    <tr><td colSpan={7} className="muted-text">No WorkSpaces users found.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
